import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import ApolloError from '@shared/components/ApolloError';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { DialogActions, DialogContent } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AnimatePresence } from 'framer-motion';
import MotionSection from '@shared/components/animations/MotionSection';
import { useUserRoleOptions } from '../../shared/hooks/useUserRoleOptions';
import FormCustomSelector from '@shared/components/form/FormCustomSelector';
import { MoveLicenseToPaidValidator } from './validators/moveLicenseToPaidValidator';
import { joiResolver } from '@hookform/resolvers/joi';
import CHANGE_USER_FREE_STATUS from '../../../queries/changeLicenseQueries/changeUserFreeStatusMutation';
import {
  FORM_ID,
  MOVE_TEXT,
  CANCEL_TEXT,
  SAVE_TEXT,
  USER_MOVED_SUCCESSFULLY_MESSAGE,
  MOVE_CONFIRMATION_MESSAGE,
  FORM_INCOMPLETE_MESSAGE
} from './constants/moveLicenseConstants';
import { MOVE_LICENSE_TYPES } from '../../../constants/MoveLicenseTypes';

/**
 * @typedef {object} MoveLicenseToPaidProps
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {number} [orgId]
 * @property {number} [orgUnitId]
 * @property {string} [previousLink]
 */

/**
 * @param {MoveLicenseToPaidProps} props
 */
const MoveLicenseToPaid = props => {
  const { dialogContent, dialogActions, externalUserId, orgId, orgUnitId, previousLink } = props;
  const navigate = useNavigate();
  const client = useApolloClient();

  const form = useForm({
    mode: 'onChange',
    resolver: joiResolver(MoveLicenseToPaidValidator.schema),
    defaultValues: {
      systemRoleId: null
    }
  });

  const { handleSubmit, trigger } = form;

  const { userRoleOptions, userRoleOptionsLoading, userRoleOptionsError } = useUserRoleOptions(
    orgId,
    orgUnitId,
    false
  );

  const [changeUserFreeStatus, { error: changeUserFreeStatusError }] = useMutation(
    CHANGE_USER_FREE_STATUS,
    {
      update: () => client.clearStore()
    }
  );

  const handleConfirm = async formData => {
    const result = await changeUserFreeStatus({
      variables: {
        moveLicenseType: MOVE_LICENSE_TYPES.PAID,
        externalUserId: externalUserId,
        systemRoleId: formData.systemRoleId
      }
    });

    if (!result.data.changeUserFreeStatus.Success) {
      showErrorToast(result.data.changeUserFreeStatus.Errors.map(e => e.Message).join(', '));
      return;
    }

    showSuccessToast(USER_MOVED_SUCCESSFULLY_MESSAGE);
    navigate(previousLink);
  };

  const openModalPreCondition = async () => {
    const validationResult = await trigger();

    if (!validationResult) {
      showErrorToast(FORM_INCOMPLETE_MESSAGE);
    }

    return validationResult;
  };

  if (changeUserFreeStatusError || userRoleOptionsError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(handleConfirm)}>
      <FormProvider {...form}>
        <DialogContent>
          {dialogContent}

          <AnimatePresence>
            <MotionSection keyProp='move-license-to-free'>
              <FormCustomSelector
                name='systemRoleId'
                id='roles-selector'
                title='User Role'
                placeholder='Select a User Role'
                testId='roles-selector'
                options={userRoleOptions}
                loading={userRoleOptionsLoading}
                menuProps={{ disablePortal: true }}
              />
            </MotionSection>
          </AnimatePresence>
        </DialogContent>
        <DialogActions>
          {dialogActions}

          <ConfirmDialogWithButton
            openModalBtnText={SAVE_TEXT}
            confirmBtnText={MOVE_TEXT}
            cancelBtnText={CANCEL_TEXT}
            message={MOVE_CONFIRMATION_MESSAGE}
            dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
            formId={FORM_ID}
            openModalPreCondition={openModalPreCondition}
          />
        </DialogActions>
      </FormProvider>
    </form>
  );
};

export default MoveLicenseToPaid;
